export const ERROR_CODES = {
	IMAGE_SAFETY: 'IMAGE_SAFETY_POLICY_VIOLATION',
	UNSUPPORTED_IMAGE: 'UNSUPPORTED_IMAGE_FORMAT',
	UNSUPPORTED_DOCUMENT: 'UNSUPPORTED_DOCUMENT_FORMAT',
	MUSIC_COPYRIGHT: 'MUSIC_COPYRIGHT_VIOLATION',
	GENERATION_FAILED: 'GENERATION_FAILED',
	/** Гость исчерпал бесплатные запросы (бэк отдаёт с 403). */
	GUEST_LIMIT: 'GUEST_LIMIT_EXCEEDED',
	LIMIT_EXCEEDED: 'FREE_LIMIT_EXCEEDED'
} as const

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES]

// Легаси-сигнал об исчерпанном лимите: до появления кодов бэк сообщал о нём только статусом.
export const LIMIT_EXCEEDED_STATUSES = [402, 429]

/** Ресурс, по которому исчерпан лимит: приходит в теле 402 как "resource". */
export type LimitResource = 'text' | 'image' | 'song'

interface ApiErrorFields {
	status?: number
	raw?: string
	retryAfter?: number
	resource?: LimitResource
	network?: boolean
	cause?: unknown
}

/**
 * Единый тип ошибки API. Всё, что летит из axios, интерцептор приводит к нему
 * (см. normalizeApiError), поэтому за пределы src/api формат AxiosError не утекает.
 */
export class ApiError extends Error {
	code?: string
	status?: number
	/** Сырое сообщение бэка — для логов и явных случаев, не для UI по умолчанию. */
	raw?: string
	/** Секунды до повторной попытки, если бэк прислал Retry-After / retry_after. */
	retryAfter?: number
	/** Какой ресурс упёрся в лимит — нужен, чтобы выбрать вариант шторки Pro. */
	resource?: LimitResource
	network: boolean
	cause?: unknown

	constructor(code?: string, message?: string, fields: ApiErrorFields = {}) {
		super(message ?? code ?? 'API error')
		this.name = 'ApiError'
		this.code = code
		this.status = fields.status
		this.raw = fields.raw
		this.retryAfter = fields.retryAfter
		this.resource = fields.resource
		this.network = fields.network ?? false
		this.cause = fields.cause
	}

	/** Совместимость с местами, которые ещё читают error.response.* напрямую. */
	get response(): unknown {
		return (this.cause as { response?: unknown } | undefined)?.response
	}
}

/** Вид исчерпанного лимита. Разделён, потому что реакция интерфейса разная. */
export type LimitKind = 'guest' | 'plan'

/**
 * Единственное место, где ошибка признаётся «лимитом».
 *
 * 402 — это всегда лимит тарифа (§5.2.1: `{ "error": "limit_exceeded", "resource": ... }`),
 * поэтому он даёт 'plan'. 429 оставляем в 'guest': это легаси-сигнал и обычный рейт-лимит,
 * а не исчерпанный тариф — реакцию на него ограничивает проверка isGuest в main.ts.
 */
export function classifyLimit(error: ApiError): LimitKind | null {
	const code = error.code?.toUpperCase()
	if (code === ERROR_CODES.GUEST_LIMIT) return 'guest'
	if (code === ERROR_CODES.LIMIT_EXCEEDED) return 'plan'
	if (error.status === 402) return 'plan'
	if (error.status && LIMIT_EXCEEDED_STATUSES.includes(error.status)) return 'guest'
	return null
}

export function isCopyrightError(code: string | null | undefined): boolean {
	return code === ERROR_CODES.MUSIC_COPYRIGHT || code === ERROR_CODES.GENERATION_FAILED
}

/** Сетевая ошибка: axios (ERR_NETWORK / нет response) или fetch (TypeError). */
export function isNetworkError(error: unknown): boolean {
	if (error instanceof ApiError) return error.network
	if (typeof error === 'object' && error !== null) {
		const e = error as { code?: string; name?: string; message?: string }
		// axios network error
		if (e.code === 'ERR_NETWORK') return true
		// fetch TypeError (сетевая проблема)
		if (e.name === 'TypeError' && e.message?.includes('Failed to fetch')) return true
	}
	return false
}

type ErrorBody = {
	code?: string
	error_code?: string
	error?: string | { code?: string; message?: string }
	message?: string
	detail?: string
	retry_after?: number | string
	resource?: string
	errors?: Record<string, string[] | undefined>
}

const LIMIT_RESOURCES: LimitResource[] = ['text', 'image', 'song']

function toLimitResource(value: unknown): LimitResource | undefined {
	return LIMIT_RESOURCES.find((resource) => resource === value)
}

function firstString(...candidates: unknown[]): string | undefined {
	for (const candidate of candidates) {
		if (typeof candidate === 'string' && candidate.trim()) return candidate
	}
	return undefined
}

function toSeconds(value: unknown): number | undefined {
	const seconds = typeof value === 'string' ? Number(value) : value
	return typeof seconds === 'number' && Number.isFinite(seconds) && seconds > 0 ? seconds : undefined
}

/** Первое сообщение из мапы валидации вида { email: ['...'] } — поле заранее не знаем. */
function firstValidationMessage(errors: ErrorBody['errors']): string | undefined {
	if (!errors || typeof errors !== 'object') return undefined
	for (const messages of Object.values(errors)) {
		if (Array.isArray(messages)) {
			const message = firstString(...messages)
			if (message) return message
		}
	}
	return undefined
}

/**
 * Единственное место в проекте, где перечислены возможные формы тела ошибки.
 * Появится новый формат — правка здесь, а не в сторах.
 */
export function normalizeApiError(error: unknown): ApiError {
	if (error instanceof ApiError) return error

	const source = (error ?? {}) as {
		response?: { status?: number; data?: unknown; headers?: unknown }
		status?: number
		message?: string
	}
	const body = (source.response?.data ?? {}) as ErrorBody
	const nestedError = typeof body.error === 'object' && body.error !== null ? body.error : undefined

	const code = firstString(body.code, body.error_code, nestedError?.code)
	const raw = firstString(
		body.message,
		body.detail,
		typeof body.error === 'string' ? body.error : undefined,
		nestedError?.message,
		firstValidationMessage(body.errors)
	)

	const headers = source.response?.headers as { 'retry-after'?: unknown } | undefined
	const retryAfter = toSeconds(headers?.['retry-after']) ?? toSeconds(body.retry_after)

	return new ApiError(code, raw ?? source.message, {
		status: source.response?.status ?? source.status,
		raw,
		retryAfter,
		resource: toLimitResource(body.resource),
		network: isNetworkError(error),
		cause: error
	})
}

/**
 * Тот же разбор для стриминга: postSSE ходит через fetch мимо axios, но формы тела
 * ошибки у бэка общие — поэтому Response приводим к виду, понятному normalizeApiError.
 * Тело читаем ровно один раз и только для неуспешного ответа.
 */
export async function apiErrorFromResponse(response: Response): Promise<ApiError> {
	const text = await response.text().catch(() => '')
	let data: unknown = {}
	try {
		if (text) data = JSON.parse(text)
	} catch {
		// Не-JSON тело (HTML от прокси, пустой ответ) — остаётся пустой объект.
	}
	return normalizeApiError({
		response: {
			status: response.status,
			data,
			// Headers — не обычный объект, а normalizeApiError читает headers['retry-after'].
			headers: Object.fromEntries(response.headers.entries())
		}
	})
}
